<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="hideWhiteBoxOnLoad || !initialFetches"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :customHeaderTabs="tabsList"
    @customTabClicked="tabClickHandler"
    @backOrCloseClicked="$emit('backOrCloseClicked')">
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <!-- Content Wrapper -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <!-- Component Wrapper -->
            <component
            v-if="initialFetches && !applyErrorState"
            :key="`animationKeyTrigger${activeComponentInfo.id}`"
            :is="getActiveComponent"
            :componentInfo="activeComponentInfo"
            :reloadFavoritesList="reloadFavoritesList"
            @rowButtonClicked="rowButtonClickedHandler"
            @rowClicked="rowClickHandler($event)"
            @reloadDone="reloadFavoritesList = false"
            @showLoader="[showLoader = $event, hideWhiteBoxOnLoad = $event]"/>
        </transition>
        <!-- Favorites Modal -->
        <favorites-modal
        v-if="showModal"
        :modalParams="modalParams"
        @hideMe="hideModalHandler"
        @requestSuccessful="modalSuccessHandler($event)"
        @requestError="modalErrorHandler($event)"/>
        <!-- FeedbackToast -->
        <toasts-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>
	</wrapper-features-templates-one-column>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue and Assets */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Aux */
import { favoritesTabsMapper, getFavoriteRoute } from '@/assets/scripts/mappers/favorites'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestTabs, clearFeatureStates } = mapActions(moduleMapper.FAVORITES)
const { getTabs } = mapGetters(moduleMapper.FAVORITES)

/*************************************************
*                  Generic Data                  *
*************************************************/
const initialFetches = ref(false)
const showLoader = ref(false)
const hideWhiteBoxOnLoad = ref(true)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const reloadFavoritesList = ref(false)

/*************************************************
*                  Modal Logic                   *
*************************************************/
const showModal = ref(false)
const modalParams = ref({})

const rowButtonClickedHandler = (data) => {
    modalParams.value = data
    showModal.value = true
}

const hideModalHandler = () => {
    showModal.value = false
    modalParams.value = {}
}

const modalSuccessHandler = (ev) => {
    showToastHandler(ev)
    hideModalHandler()
    reloadFavoritesList.value = true
}

const modalErrorHandler = (ev) => {
    showToastHandler(ev)
    hideModalHandler()
}

/*************************************************
*                    Tabs Logic                  *
*************************************************/
const tabsList = ref([])

const parseTabs = () => {
    // Parse the tabs to include the label and selected params
    tabsList.value = getTabs.value.map((tab, index) => {
        return {
            ...tab,
            label: tab.description,
            selected: index === 0
        }
    })
    // Set active component to be the first on the tab list
    activeComponentInfo.value = getTabs.value[0]
}

const tabClickHandler = (event) => {
    // Set selected
    tabsList.value.forEach(tab => {
        tab.selected = tab.id === event
    })
    // Set active component to be the one clicked
    activeComponentInfo.value = getTabs.value.find(tab => tab.id === event)
}

/*************************************************
*                  Content Logic                 *
*************************************************/
const activeComponentInfo = ref(null)
const getActiveComponent = computed(() => {
    const parsedActiveComponent = favoritesTabsMapper(activeComponentInfo.value.id)

    return defineAsyncComponent(() => import('@/components/features/favorites/' + parsedActiveComponent))
})

const rowClickHandler = (data) => {
    if (data.operationTypeId === 74 || data.operationTypeId === 75 || data.operationTypeId === 32009 || data.operationTypeId === 500016) {
        navigateByName(getFavoriteRoute(data.operationTypeId), { favoriteInfo: data.params })
    } else navigateByName(getFavoriteRoute(data.operationTypeId), { params: data.params })
}

/*************************************************
*                 Requests Logic                 *
*************************************************/
const initialRequests = async () => {
    showLoader.value = true
    applyErrorState.value = false
    showFeedbackToast.value = false

    try {
        await requestTabs()
        parseTabs()
        initialFetches.value = true
        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
    }
}

/*************************************************
*                 Error Handler                  *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
    showFeedbackToast.value = true
}

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showToastHandler = response => {
    let displayText = null
    const activeTab = activeComponentInfo.value.id
    const modalType = modalParams.value.type
    const errorLevel = response.errorLevel.toLowerCase()

    const removeSuccessOptions = {
        1: 'favorites.transfers.removeSuccessText',
        2: 'favorites.payments.removeSuccessText',
        3: 'favorites.smartTransfer.removeSuccessText',
        4: 'favorites.cards.removeSuccessText',
        5: 'favorites.withdrawals.removeSuccessText',
        6: 'favorites.transfers.removeSuccessText'
    }

    if (modalType === 'remove') {
        displayText = errorLevel === 'error' ? t('genericErrorMessage') : t(removeSuccessOptions[activeTab])
    } else {
        displayText = response.message || t('genericErrorMessage')
    }

    feedbackToastData.value = {
        text: displayText,
        toastType: errorLevel
    }
    showFeedbackToast.value = true
}

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.generic-tabs-wrapper {
    margin-bottom: 16px;
}
</style>
